import {Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ApiService} from '../../shared/services/api.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {LocationService} from '../../shared/services/location.service';
import {CookieService} from "ngx-cookie-service";
import { GoogleAnalyticsService } from '../../shared/services/google-analytics.service';
import { RegisterValidation } from '../../shared/validations/register.validation';
import appConfig from '../../shared/config/app-config';
import { verifiedImage, signupTick } from '../../utils/images';
import { MyAccountService } from '../../shared/services/my-account.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent {

  form!: FormGroup;
  curr_step: number | undefined;
  token: string | null = null;
  showPassword: boolean = false;
  showRcvs: boolean = true;
  errorMessage: any = {};
  loader: boolean = false;
  showResendLink: boolean = false;
  countries: any = [];
  rcvsFieldsName: string = '';
  isDisabled: boolean = true;
  vettimesUrl: string = appConfig.vettimesUrl
  verifiedImage = verifiedImage
  signupTick = signupTick

  constructor(
    private apiService: ApiService,
    private myAccountService: MyAccountService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private locationService: LocationService,
    private cookieService: CookieService,
    private googleAnalytics: GoogleAnalyticsService
  ) {
    this.form = RegisterValidation.createRegisterForm(this.formBuilder);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['token']) {
        this.emailVerification(params['token']);
      } else {
        this.curr_step = 1;
      }
    });

    this.getCountries();
  }

  setCurrentLocation() {

    this.locationService.getIpAddress().subscribe(data => {

      const ipAddress = data.ip

      this.locationService.getLocation(ipAddress).subscribe(res => {
        const countryCode = res.data?.country_code;
        
        if (countryCode) {
          const country = this.countries.find((c: any) => (c.country_code || '').toLowerCase() === countryCode.toLowerCase())

          if (country) {
            this.form.patchValue({country_of_residence: country?.country_name});
            this.onCountryChange();
          }else {
            this.form.patchValue({country_of_residence: ''});
            this.onCountryChange();
          }

        }
      }, error => {
        console.error('Error fetching location', error);
      })
    }, error => {
      console.error('Error fetching IP address', error);
    })
  }

  trackButtonClick(event: any) {

    const nameControl = this.form.get('name');
    const lastNameControl = this.form.get('last_name');
    const emailControl = this.form.get('email');
    const passwordControl = this.form.get('password');

    if (event.button === 'Create account') {
      event.payload = this.getBody()
      delete event.payload.password;

      this.form.markAllAsTouched();
      if (this.form.invalid) {
        return;
      }

    } else if (event.button === 'Next') {
      if (
        nameControl?.invalid ||
        lastNameControl?.invalid ||
        emailControl?.invalid ||
        passwordControl?.invalid
      ) {
        return;
      }
    }


    this.googleAnalytics.trackButtonClick(event);
  }

  getCountries() {
    this.myAccountService.httpGet('user/countries').subscribe(
      (res: any) => {
        this.countries = res.data;
        this.setCurrentLocation();
      },
      (err: any) => {
        console.error('Error fetching countries', err);
      }
    );
  }

  togglePasswordVisibility(e: Event): void {
    e.preventDefault();
    this.showPassword = !this.showPassword;
  }

  next(): void {
    const stepOneFields = ['name', 'last_name', 'email', 'password'];
    stepOneFields.forEach(field => this.form.get(field)?.markAsTouched());
    const hasStepOneErrors = stepOneFields.some(field => this.form.get(field)?.invalid);
    if (hasStepOneErrors) return;
    this.checkEmail();
  }

  checkEmail() {
    this.loader = true;
    const {email} = this.form.value;
    this.apiService.httpPost('auth/check-email', {email}).subscribe(
      () => {
        this.curr_step = 2;
        this.loader = false;
        this.errorMessage.email = '';
      },
      (err) => {
        this.errorMessage.email = err.error.errors.email;
        this.loader = false;
        this.showResendLink = this.errorMessage.email !== 'Email is already registered';
      }
    );
  }

  selectJobRole(e: MouseEvent, role: any): void {
    e.preventDefault();
    this.form.patchValue({job_role: role});
    this.jobTypeOtherValidation()
    this.rcvsValidation();
    this.isDisable();
  }

  jobTypeOtherValidation() {
    const jobTypeOtherControl = this.form.get('job_types_other');
    const role = this.form.value['job_role'];

    if (['O'].includes(role)) {
      const validators = [Validators.required];
      jobTypeOtherControl?.setValidators(validators);
    } else  {
      jobTypeOtherControl?.clearValidators();
      jobTypeOtherControl?.setValue('');
    }
    jobTypeOtherControl?.updateValueAndValidity();
  }

  onCountryChange() {
    this.rcvsValidation()
    this.validateRcvsInput()
  }

  rcvsValidation() {
    const rcvsControl = this.form.get('rcvs');
    const role = this.form.value['job_role'];
    const country = this.countries.find((c: any) => c.country_name === this.form.get('country_of_residence')?.value);
    const isUK = country?.country_code?.startsWith('GB');

    if (['C', 'D', 'G'].includes(role)) {
      this.rcvsFieldsName = isUK ? 'RCVS number' : 'Membership number';
      const validators = [Validators.pattern(/^[a-zA-Z0-9]+$/)];

      if (isUK) {
        validators.push(Validators.minLength(7), Validators.maxLength(7));
      }

      rcvsControl?.setValidators(validators);
      this.showRcvs = true;
    } else if (['S', 'O'].includes(role)) {
      rcvsControl?.clearValidators();
      this.showRcvs = false;
    }
    rcvsControl?.setValue('');
    rcvsControl?.updateValueAndValidity();
  }

  findRcvsNumber(e: any): void {
    e.preventDefault();
    window.open(appConfig.rcvsUrl, '_blank');
  }

  validateRcvsInput() {
    const input = document.getElementById('rcvs') as HTMLInputElement;
    let value = input.value;

    let validValue = value.charAt(0).replace(/[^a-zA-Z0-9]/, '');

    if (this.rcvsFieldsName == 'RCVS number' && value.length < 8) {

      validValue += value.slice(1, 6).replace(/[^0-9]/g, '');

      if (value.length > 6) {
        validValue += value.charAt(6).replace(/[^a-zA-Z0-9]/, '');
      }

      validValue = validValue.slice(0, 7);

    } else if (this.rcvsFieldsName == 'Membership number') {
      validValue += value.slice(1).replace(/[^0-9]/g, '');
    }

    if (value !== validValue) {
      this.form.get('rcvs')?.setValue(validValue, { emitEvent: false });
      return;
    }

    this.errorMessage.rcvs = '';
    this.isDisable();
  }

  isDisable() {
    this.isDisabled = this.form.invalid || !this.form.value['agreeTerms'] ? true : false;
  }

  getBody(): any {
    const formData = { ...this.form.value, password: this.form.value.password.trim() };
    if (this.rcvsFieldsName === 'Membership number') {
      formData['membership'] = formData['rcvs'];
    }
    if (!this.showRcvs || this.rcvsFieldsName === 'Membership number' || !formData['rcvs']) {
      delete formData['rcvs'];
    }
    return formData;
  }

  register() {
    this.loader = true;
    this.apiService.httpPost('auth/register', this.getBody()).subscribe(
      () => {
        this.loader = false;
        this.curr_step = 3;
      },
      (err: any) => {
        this.errorMessage.rcvs = err.error.errors.rcvs;
        this.loader = false;
      }
    );
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.register();
  }

  resendVerificationEmail(e: any) {
    e.preventDefault();
    this.loader = true;
    const {email} = this.form.value;
    this.apiService.httpPost('auth/resend-email', {email}).subscribe((res: any) => {
        this.toastr.success(res.message);
        this.loader = false;
      }, (error: any) => {
        this.toastr.error(error.error.errors);
        this.loader = false;
      }
    );
  }

  emailVerification(token: any) {
    this.apiService.httpGet(`auth/verify-email?token=${token}`).subscribe(
      (res: any) => {

        let user = res.data.user;
        localStorage.setItem('user', JSON.stringify(user))
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + 60); // Adds 60 days
        this.cookieService.set('sso_signed', res.data.token, expireDate, '/', appConfig.subDomain);
        this.loader = false;
        this.curr_step = 5;
      },
      () => {
        this.curr_step = 4;
        this.loader = false;
      }
    );
  }

  productLink() {
    window.open(appConfig.myJobsUrl);
  }
}
